import {
    TOPIC_SYSLOG_CHUNK_READY,
    TOPIC_SYSLOG_HYDRATION_REQUEST,
} from 'lib/ws/constants'
import type { Service, Hub } from 'lib/ws/election'
import { getWsHub } from 'lib/ws/hub'

export type ChunkReadyPayload = {
    chunkId: string
}

export type HydrationRequestPayload = {
    sinceUid: string
    untilUid: string
}

/**
 * Multi-tab coordination service for the syslog viewer.
 *
 * - Leader tab owns OPFS writes (hydration, manifest updates).
 * - All tabs can read from OPFS concurrently.
 * - Leader broadcasts chunk-ready events so reader tabs refresh their view.
 * - Follower tabs send hydration requests to the leader via the hub.
 */
export class SyslogService implements Service {
    public readonly namespace = 'syslog'
    private isLeaderTab = false
    private onChunkReadyCallbacks: Array<(payload: ChunkReadyPayload) => void> = []
    private onHydrationRequestCallbacks: Array<(payload: HydrationRequestPayload) => void> = []

    public async init(_hub?: Hub): Promise<void> {
        const hub = getWsHub()
        if (!hub) return

        // All tabs: listen for chunk-ready notifications from the leader
        hub.subscribe<ChunkReadyPayload>(TOPIC_SYSLOG_CHUNK_READY, (payload) => {
            for (const cb of this.onChunkReadyCallbacks) {
                cb(payload)
            }
        })

        // Leader only: listen for hydration requests from follower tabs
        hub.subscribe<HydrationRequestPayload>(TOPIC_SYSLOG_HYDRATION_REQUEST, (payload) => {
            if (!this.isLeaderTab) return
            for (const cb of this.onHydrationRequestCallbacks) {
                cb(payload)
            }
        })
    }

    /**
     * Called when this tab becomes the leader.
     */
    public async onBecomeLeader(): Promise<void> {
        this.isLeaderTab = true
    }

    /**
     * Broadcast that a chunk has finished hydration (leader only).
     */
    // eslint-disable-next-line class-methods-use-this
    public notifyChunkReady(chunkId: string): void {
        const hub = getWsHub()
        hub?.publish(TOPIC_SYSLOG_CHUNK_READY, { chunkId } satisfies ChunkReadyPayload)
    }

    /**
     * Request the leader tab to hydrate a time range (follower only).
     */
    // eslint-disable-next-line class-methods-use-this
    public requestHydration(sinceUid: string, untilUid: string): void {
        const hub = getWsHub()
        hub?.publish(TOPIC_SYSLOG_HYDRATION_REQUEST, { sinceUid, untilUid } satisfies HydrationRequestPayload)
    }

    /**
     * Whether this tab is the hydration leader.
     */
    public isLeader(): boolean {
        return this.isLeaderTab
    }

    /**
     * Register a callback for when a new chunk is ready (any tab).
     */
    public onChunkReady(cb: (payload: ChunkReadyPayload) => void): () => void {
        this.onChunkReadyCallbacks.push(cb)
        return (): void => {
            this.onChunkReadyCallbacks = this.onChunkReadyCallbacks.filter((c) => c !== cb)
        }
    }

    /**
     * Register a callback for hydration requests (leader tab only).
     */
    public onHydrationRequest(cb: (payload: HydrationRequestPayload) => void): () => void {
        this.onHydrationRequestCallbacks.push(cb)
        return (): void => {
            this.onHydrationRequestCallbacks = this.onHydrationRequestCallbacks.filter((c) => c !== cb)
        }
    }
}

// Singleton instance
let syslogService: SyslogService | undefined

export function getSyslogService(): SyslogService | undefined {
    return syslogService
}

export function createSyslogService(): SyslogService {
    if (!syslogService) {
        syslogService = new SyslogService()
    }
    return syslogService
}
